import groupBy from 'lodash/groupBy';

// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { fetchQuery, graphql } from 'react-relay';
// eslint-disable-next-line jira/import-whitelist
import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import type { getPriorityGroupDataByProjectIdQuery } from '@atlassian/jira-relay/src/__generated__/getPriorityGroupDataByProjectIdQuery.graphql';
import type { CategorizedGroupData, PriorityGroup, QueueId } from '../../types.tsx';

type GroupResponse = PriorityGroup & {
	queueIds: QueueId[];
	category: string;
};

type GroupFetchResponse = {
	results: GroupResponse[];
};

const fetchGroupsByKey = async (projectKey: string, cloudId: string) => {
	return fetchJson(
		`/gateway/api/jsm/queues/cloudid/${cloudId}/api/v1/groups?projectIdOrKey=${projectKey}&operation=fetchPriorityGroups`,
	);
};

const fetchGroupsByIdGraphql = async (projectId: number, cloudId: string) => {
	const projectAri = `ari:cloud:jira:${cloudId}:project/${projectId}`;
	const result = await fetchQuery<getPriorityGroupDataByProjectIdQuery>(
		getRelayEnvironment(),
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			query getPriorityGroupDataByProjectIdQuery($projectAri: ID!) {
				queuesprioritygroups_priorityGroups(projectId: $projectAri) {
					results {
						id
						name
						category
						queueIds
						version
						creationMethod
					}
				}
			}
		`,
		{ projectAri },
	).toPromise();

	if (!result?.queuesprioritygroups_priorityGroups?.results) {
		fireErrorAnalytics({
			meta: {
				id: 'priorityGroupByProjectIdNoPriorityGroupsGraphql',
				packageName: 'jiraRouterResourcesServiceDeskQueues',
				teamName: 'jsm-flash',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	}

	return {
		results:
			result?.queuesprioritygroups_priorityGroups?.results?.reduce(
				(priorityGroups, priorityGroup) => {
					// this mapping function is to make types happy
					if (
						priorityGroup &&
						priorityGroup.id &&
						priorityGroup.name &&
						priorityGroup.category &&
						priorityGroup.queueIds
					) {
						priorityGroups.push({
							id: priorityGroup.id,
							name: priorityGroup.name,
							category: priorityGroup.category,
							queueIds: priorityGroup.queueIds.reduce((queueIds, queueId) => {
								if (queueId !== null && queueId !== undefined) {
									queueIds.push(queueId);
								}
								return queueIds;
								// we need the below, otherwise reduce things the type is never[]
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							}, [] as QueueId[]),
							...(priorityGroup.version ? { version: priorityGroup.version } : {}),
							...(priorityGroup.creationMethod
								? { creationMethod: priorityGroup.creationMethod }
								: {}),
						});
					}
					return priorityGroups;
				},
				// we need the below, otherwise reduce things the type is never[]
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				[] as GroupResponse[],
			) ?? [],
	};
};

export const getPriorityGroupDataByProjectId = async (
	projectId: number | undefined | null,
	projectKey: string,
	cloudId: string,
) => {
	let data: GroupFetchResponse;
	if (projectId) {
		data = await fetchGroupsByIdGraphql(projectId, cloudId);
	} else {
		fireErrorAnalytics({
			meta: {
				id: 'fetchPriorityGroupsProjectIdUndefined',
				packageName: 'jiraRouterResourcesServiceDeskQueues',
				teamName: 'jsm-flash',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
		data = await fetchGroupsByKey(projectKey, cloudId);
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const processedData = groupBy(
		data.results.map((group) => ({ ...group, queues: group.queueIds || [] })),
		'category',
	) as unknown;

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return processedData as CategorizedGroupData;
};
